/**
 * @file NewCustomerForm.tsx
 * @description Creates a customer record without email and captures reusable contacts.
 * @module Components/Customers
 * @security PII is submitted only to the admin-gated API and encrypted server-side.
 */

import type { FormEvent, JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../../lib/api';
// prettier-ignore
import { createCustomer, type CustomerContact, type CustomerContactType } from '../../lib/admin-api';
import { Button } from '../common/Button';
import { FormField } from '../common/FormField';

interface NewCustomerFormProps {
  locale: 'he' | 'en';
  onCreated: () => void;
}

export function NewCustomerForm({ locale, onCreated }: NewCustomerFormProps): JSX.Element {
  const { t } = useTranslation();
  const [fullName, setFullName] = useState('');
  const [contacts, setContacts] = useState<CustomerContact[]>([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ error: boolean; text: string } | null>(null);

  const onSubmit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      await createCustomer({ full_name: fullName.trim(), locale, contacts });
      setMessage({ error: false, text: t('customers.new_success', { name: fullName.trim() }) });
      setFullName('');
      setContacts([]);
      onCreated();
    } catch (error) {
      const text = error instanceof ApiError ? error.problem.detail : t('customers.errors.create');
      setMessage({ error: true, text });
    } finally {
      setSaving(false);
    }
  };

  const addContact = (type: CustomerContactType): void => {
    setContacts((current) => [
      ...current,
      {
        id: crypto.randomUUID(),
        type,
        label: null,
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
      },
    ]);
  };

  return (
    <form onSubmit={onSubmit} className="card p-4 space-y-4">
      <p className="text-sm font-semibold text-slate-900">{t('customers.new_title')}</p>
      <FormField
        id="new_customer_name"
        label={t('customers.new_name_label')}
        value={fullName}
        onChange={(e) => setFullName(e.target.value)}
        placeholder={t('customers.new_name_placeholder')}
        required
      />
      <ContactButtons add={addContact} />
      {contacts.map((contact, index) => (
        <ContactFields
          key={contact.id}
          contact={contact}
          index={index}
          onChange={(next) =>
            setContacts((rows) => rows.map((row, i) => (i === index ? next : row)))
          }
          onRemove={() => setContacts((rows) => rows.filter((_, i) => i !== index))}
        />
      ))}
      <div className="flex justify-end">
        <Button type="submit" loading={saving}>
          {t('customers.new_submit')}
        </Button>
      </div>
      {message !== null ? (
        <p
          role={message.error ? 'alert' : undefined}
          className={message.error ? 'text-xs text-red-600' : 'text-xs text-emerald-700'}
        >
          {message.text}
        </p>
      ) : null}
    </form>
  );
}

function ContactButtons({ add }: { add: (type: CustomerContactType) => void }): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap gap-2">
      <Button
        type="button"
        className="bg-slate-100 text-slate-700 hover:bg-slate-200"
        onClick={() => add('finance')}
      >
        {t('customers.contacts.add_finance')}
      </Button>
      <Button
        type="button"
        className="bg-slate-100 text-slate-700 hover:bg-slate-200"
        onClick={() => add('secretary')}
      >
        {t('customers.contacts.add_secretary')}
      </Button>
      <Button
        type="button"
        className="bg-slate-100 text-slate-700 hover:bg-slate-200"
        onClick={() => add('other')}
      >
        {t('customers.contacts.add_other')}
      </Button>
    </div>
  );
}

interface ContactFieldsProps {
  contact: CustomerContact;
  index: number;
  onChange: (contact: CustomerContact) => void;
  onRemove: () => void;
}

function ContactFields({ contact, index, onChange, onRemove }: ContactFieldsProps): JSX.Element {
  const { t } = useTranslation();
  const set = (field: keyof CustomerContact, value: string): void =>
    onChange({ ...contact, [field]: value });
  return (
    <fieldset className="rounded-lg border border-slate-200 p-3 space-y-2">
      <legend className="px-1 text-sm font-semibold">
        {t(`customers.contacts.type.${contact.type}`)}
      </legend>
      {contact.type === 'other' ? (
        <FormField
          id={`contact_${index}_label`}
          label={t('customers.contacts.label')}
          value={contact.label ?? ''}
          onChange={(e) => set('label', e.target.value)}
        />
      ) : null}
      <div className="grid gap-2 md:grid-cols-2">
        <FormField
          id={`contact_${index}_first`}
          label={t('customers.contacts.first_name')}
          value={contact.first_name}
          onChange={(e) => set('first_name', e.target.value)}
          required
        />
        <FormField
          id={`contact_${index}_last`}
          label={t('customers.contacts.last_name')}
          value={contact.last_name}
          onChange={(e) => set('last_name', e.target.value)}
          required
        />
        <FormField
          id={`contact_${index}_email`}
          type="email"
          label={t('customers.contacts.email')}
          value={contact.email}
          onChange={(e) => set('email', e.target.value)}
          required
        />
        <FormField
          id={`contact_${index}_phone`}
          type="tel"
          label={t('customers.contacts.phone')}
          value={contact.phone}
          onChange={(e) => set('phone', e.target.value)}
          required
        />
      </div>
      <button type="button" onClick={onRemove} className="text-sm text-red-600 hover:underline">
        {t('customers.contacts.remove')}
      </button>
    </fieldset>
  );
}
